// src/components/modals/ProviderPreviewModal.js
import React, { useEffect, useState } from "react";

function ProviderPreviewModal({
  open,
  onBack,
  onClose,
  preview, // ProviderPreviewDTO
  npi, // string (10 digits)
  onContinue,
}) {
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null);
  }, [open]);

  if (!open) return null;

  const claimStatus = preview?.claimStatus || "UNCLAIMED";
  const verificationStatus = preview?.verificationStatus || "UNVERIFIED";
  const claimed =
    claimStatus === "CLAIMED" || verificationStatus === "VERIFIED";
  const pendingReview =
    verificationStatus === "PENDING_REVIEW" ||
    verificationStatus === "DOCUMENTS_REQUESTED";
  const statusLabel = claimed
    ? "CLAIMED"
    : verificationStatus === "PENDING_REVIEW"
      ? "PENDING REVIEW"
      : verificationStatus === "DOCUMENTS_REQUESTED"
        ? "DOCUMENTS REQUESTED"
        : claimStatus;

  const fullName =
    `${preview?.firstName || ""} ${preview?.lastName || ""}`.trim() || "N/A";
  const specialty = preview?.specialty || "N/A";
  const locations = Array.isArray(preview?.locations) ? preview.locations : [];

  const canContinue = !claimed && !pendingReview;

  const handleThisIsMe = () => {
    if (!npi) {
      setMsg("Missing NPI. Please search again.");
      return;
    }

    onContinue?.(npi);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 relative">
        {/* Close (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2 text-center">Is this you?</h2>

        {/* Subtext */}
        <p className="text-sm text-gray-600 mb-5 text-center">
          Confirm your provider profile before continuing.
        </p>

        {/* Provider Card */}
        <div className="border rounded-lg p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                {fullName}
              </div>
              <div className="text-sm text-gray-700 mt-1">
                <span className="font-semibold text-gray-900">Specialty:</span>{" "}
                <span className="text-gray-700">{specialty}</span>
              </div>
            </div>

            {/* Claim badge */}
            <div className="flex items-center gap-2">
              {claimed && (
                <span
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-600 text-white text-sm"
                  title="Claimed"
                  aria-label="Claimed"
                >
                  ✓
                </span>
              )}
              <span className="text-xs font-semibold px-2 py-1 rounded border text-gray-700">
                {statusLabel}
              </span>
            </div>
          </div>

          <div className="mt-3">
            <div className="text-sm font-semibold text-gray-900">
              Locations:
            </div>
            {locations.length ? (
              <ul className="mt-1 space-y-1">
                {locations.map((l, idx) => (
                  <li
                    key={`${l.city}-${l.state}-${idx}`}
                    className="text-sm text-gray-700"
                  >
                    {l.city}, {l.state}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-gray-500 mt-1">N/A</div>
            )}
          </div>
        </div>

        {/* Claimed / pending guidance */}
        {claimed && (
          <div className="mt-4 text-sm text-gray-700">
            This profile is already claimed. If this isn’t you, contact MedMaze
            support.
          </div>
        )}

        {!claimed && pendingReview && (
          <div className="mt-4 text-sm text-gray-700">
            This profile already has a verification request under review.
          </div>
        )}

        {msg && <div className="mt-3 text-sm text-red-600">{msg}</div>}

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onBack || onClose}
            className="px-3 py-2 rounded border text-gray-700"
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleThisIsMe}
            disabled={!canContinue}
            className={`px-4 py-2 rounded ${
              canContinue
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-gray-200 text-gray-500 cursor-not-allowed"
            }`}
          >
            This is me
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProviderPreviewModal;
